import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import mt from 'moment-timezone';

import { Time } from '../Time';
import { Pluralize } from '../Pluralize';

const TimeAgo = ({
  timestamp,
  format = 'MMM DD, YYYY [at] hh:mm A',
  label = '',
  short = false
}) => {
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const formatTimeAgoOrDate = useCallback(() => {
    const now = moment();
    const targetDate = mt(moment.utc(timestamp)).tz(userTimeZone);

    // Calculate the difference in seconds, minutes, and hours
    const secondsDiff = now.diff(targetDate, 'seconds');
    const minutesDiff = now.diff(targetDate, 'minutes');
    const hoursDiff = now.diff(targetDate, 'hours');

    if (short) {
      if (secondsDiff < 60) {
        return '1m ago';
      }
      if (minutesDiff < 60) {
        return <>{minutesDiff}m ago</>;
      }
    } else {
      if (secondsDiff < 60) {
        return 'a few seconds ago';
      }
      if (minutesDiff < 60) {
        return (
          <>
            <Pluralize word='minutes' count={minutesDiff} /> ago
          </>
        );
      }
    }

    if (hoursDiff < 24) {
      return (
        <>
          <Pluralize word='hour' count={hoursDiff} /> ago
        </>
      );
    }

    if (label) {
      return (
        <>
          {label} <Time time={targetDate.toString()} format={format} />
        </>
      );
    }

    // If it's more than 24 hours ago, show the date
    return <Time time={targetDate.toString()} format={format} />;
  }, [timestamp]);

  return <>{formatTimeAgoOrDate(timestamp)}</>;
};

TimeAgo.propTypes = {
  timestamp: PropTypes.string.isRequired,
  format: PropTypes.string,
  label: PropTypes.string,
  short: PropTypes.bool
};

export { TimeAgo };
